import React from 'react';
import clsx from 'clsx';
import { CarDetailsAssessmentProps } from './CarDetailsAssessment.types';
import { Badge } from '../Badge/Badge';
import { BadgeStatusType } from '../Badge/BadgeTypes';
import { IconName, SVGIcon } from '../SVGIcon';

const AssessmentCell: React.FC<{
  iconUrl?: string;
  title: string;
  title2?: string;
  status?: BadgeStatusType;
  badgeText?: string;
  description?: string;
  index: number;
  totalItems: number;
  customBody?: React.ReactNode;
}> = ({
  iconUrl,
  title,
  title2,
  status,
  description,
  index,
  totalItems,
  badgeText,
  customBody,
}) => {
    // Dla 1 elementu nie dodajemy border-r
    // Dla wielokrotności 3 - usuwamy border-r dla ostatniego w każdym rzędzie
    const shouldHaveBorderRight = () => {
      if (totalItems === 1) return false;
      if (totalItems % 3 === 0) return (index + 1) % 3 !== 0; // Nie jest ostatni w rzędzie
      if (totalItems % 4 === 0) return (index + 1) % 4 !== 0; // Nie jest ostatni w rzędzie
    };

    // If customBody is provided, render it instead of default layout
    if (customBody) {
      return (
        <div
          className={clsx(
            'flex flex-col flex-1 self-start w-full h-full border md:border-b md:border-l-0 md:border-t-0 border-zinc-200 rounded-card md:rounded-none',
            shouldHaveBorderRight() ? 'md:border-r' : 'md:border-r-0',
          )}
        >
          {customBody}
        </div>
      );
    }

    return (
      <div
        className={clsx(
          'flex flex-col flex-1 w-full h-full border md:self-start md:border-l-0 md:border-t-0 md:border-b border-zinc-200 rounded-card md:rounded-none',
          shouldHaveBorderRight() ? 'md:border-r' : 'md:border-r-0',
        )}
      >
        {/* Mobile: kompaktowy poziomy layout */}
        <div className="flex flex-col gap-3 p-5 md:hidden">
          <div className="flex flex-row items-center justify-between gap-3">
            {iconUrl && (
              <div className="flex h-10 w-10 items-center justify-center rounded-card bg-slate-100 px-1">
                <SVGIcon name={iconUrl as IconName} size="md" fill="primary" />
              </div>
            )}
            {status && (
              <Badge variant={status} badgeText={badgeText} version="v2" />
            )}
          </div>
          <div className="flex flex-col gap-1">
            {title2 && (
              <div className="text-2xl font-semibold leading-snug text-zinc-800">
                {title2}
              </div>
            )}
            <div className="text-base font-semibold leading-snug text-zinc-800">
              {title}
            </div>
            {description && (
              <div className="text-sm text-grayText">
                {description}
              </div>
            )}
          </div>
        </div>

        {/* Desktop: oryginalny pionowy layout */}
        <div className="hidden md:flex md:flex-col md:flex-1">
          <div className="flex flex-col items-center my-9 max-w-full">
            {iconUrl && (
              <div className="flex h-[62px] w-[62px] items-center justify-center gap-2.5 rounded-card bg-slate-100 px-1">
                <SVGIcon name={iconUrl as IconName} size='lg' fill='primary' />
              </div>
            )}
            {title2 && (
              <div className="mt-5 text-3xl font-semibold leading-snug text-center text-zinc-800">
                {title2}
              </div>
            )}
            <div className="mt-5 text-xl font-semibold leading-snug text-center text-zinc-800">
              {title}
            </div>
          </div>
          <div className="flex flex-col items-center mb-8">
            {status && (
              <Badge variant={status} classNames="mx-auto" badgeText={badgeText} version='v2' />
            )}
            {description && (
              <div className="mt-8 text-sm text-center text-grayText">
                {description}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

const CarDetailsAssessment: React.FC<CarDetailsAssessmentProps> = ({
  assessmentItems,
  summaryIcon,
  summaryTitle,
  summaryContent,
  className,
}) => {
  const itemCount = assessmentItems.length;

  // Określamy liczbę kolumn i rzędów na podstawie liczby elementów
  const getGridClasses = () => {
    if (itemCount === 1) {
      return 'grid grid-cols-1 grid-rows-1';
    }
    if (itemCount === 2) {
      return 'grid grid-cols-2 grid-rows-1';
    }
    // Dla wielokrotności 3
    const rows = Math.ceil(itemCount / 3);
    if (itemCount % 3 === 0) {
      return `grid grid-cols-1 md:grid-cols-3 md:grid-rows-${rows}`;
    }
    if (itemCount % 4 === 0) {
      return `grid grid-cols-1 md:grid-cols-4 md:grid-rows-${rows}`;
    } else
      return `grid grid-cols-1 md:grid-cols-3 md:grid-rows-${rows}`;
  };

  return (
    <div
      className={clsx(
        'flex flex-col rounded-xl md:border border-solid border-zinc-200',
        className,
        {
          'md:min-w-[350px]': itemCount === 1,
          'md:min-w-[750px]': itemCount > 1,
        },
      )}
    >
      <div
        className={clsx(
          'z-10 grid-flow-row gap-4 justify-between w-full md:gap-0 max-md:max-w-full',
          getGridClasses(),
        )}
      >
        {assessmentItems.map((item, index) => (
          <React.Fragment key={item.id}>
            <AssessmentCell
              iconUrl={item.iconUrl}
              title={item.title}
              title2={item.title2}
              status={item.status}
              badgeText={item.badgeText}
              description={item.description}
              index={index}
              totalItems={itemCount}
              customBody={item.customBody}
            />
          </React.Fragment>
        ))}
      </div>
      <div className="flex overflow-hidden flex-col mt-4 w-full border md:rounded-t-none max-md:max-w-full md:mt-0 rounded-card md:rounder-none md:border-0 border-zinc-200">
        <div
          className="px-5 py-8 rounded-b-card"
          style={{
            background:
              'linear-gradient(to bottom, #f6f8fc 0%, #fdfdff 57.95%)',
          }}
        >
          <div className="flex flex-wrap gap-3 items-center text-lg font-bold leading-none text-slate-700">
            <div className="flex h-[36px] w-[36px] items-center justify-center rounded-[10px] bg-primary"><SVGIcon name={summaryIcon ?? 'airplane'} fill='white' /></div>
            <div className="flex-1">
              {summaryTitle}
            </div>
          </div>
          <div className="mt-3 text-base leading-6 text-slate-500 max-md:max-w-full">
            {summaryContent?.split('\n').map((line, index) => (
              <React.Fragment key={index}>
                {line}
                <br />
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarDetailsAssessment;
